import { useState, useEffect, useRef } from 'react';
import { LandingNavbar, LandingFooter } from '@/components/landing';
import { useCal } from '@/hooks/useCal';
import { 
  ArrowRight, 
  CheckCircle2, 
  TrendingUp, 
  Zap, 
  Globe, 
  ShieldCheck, 
  Activity, 
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// --- ReflowAI Design System (Strict Blue/Sapphire Theme) ---
const COLORS = {
  brilliance: '#FCFDFD',      // Main Background
  springtime: '#EDEFF3',      // Glass Card Background
  windweaver: '#C6D1D7',      // Borders
  sapphire: '#2F7EDA',        // Primary Brand Color
  blackwater: '#555663',      // Body Text
  darkText: '#0F172A',        // Headings
};

// --- Global Styles ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

    :root {
      --font-primary: 'Plus Jakarta Sans', sans-serif;
    }

    body {
      font-family: var(--font-primary);
      color: ${COLORS.blackwater};
      background-color: ${COLORS.brilliance};
      overflow-x: hidden;
    }

    h1, h2, h3, h4, h5, h6 {
      font-family: var(--font-primary);
      color: ${COLORS.darkText};
      letter-spacing: -0.02em;
    }

    /* Halo Gradient Background */
    .halo-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -1;
      pointer-events: none;
      overflow: hidden;
    }

    .halo-gradient {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
      animation: pulse-halo 10s ease-in-out infinite;
    }

    @keyframes pulse-halo {
      0%, 100% { transform: scale(1); opacity: 0.4; }
      50% { transform: scale(1.1); opacity: 0.6; }
    }

    /* Floating Glass Orbs */
    .glass-orb {
      background: linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.1) 100%);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border: 1px solid rgba(255,255,255,0.6);
      box-shadow: inset 0 4px 10px rgba(255,255,255,0.8), 0 10px 30px rgba(47, 126, 218, 0.15);
      border-radius: 50%;
      position: absolute;
      z-index: 0;
      animation: float-orb 8s ease-in-out infinite;
    }

    @keyframes float-orb {
      0%, 100% { transform: translateY(0) scale(1); }
      50% { transform: translateY(-20px) scale(1.05); }
    }

    /* Glass Cards */
    .glass-card {
      background: linear-gradient(145deg, rgba(255,255,255,0.7) 0%, rgba(237,239,243,0.5) 100%);
      backdrop-filter: blur(24px);
      border: 1px solid ${COLORS.windweaver};
      transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    }

    .glass-card:hover {
      background: linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(237,239,243,0.8) 100%);
      border-color: ${COLORS.sapphire};
      transform: translateY(-5px) scale(1.01);
      box-shadow: 0 25px 50px -12px rgba(47, 126, 218, 0.15);
    }
    
    .pricing-feature-list li {
      display: flex;
      align-items: start;
      gap: 12px;
      margin-bottom: 12px;
      font-size: 0.95rem;
      color: #555663;
    }

    .pricing-feature-list li svg {
      flex-shrink: 0;
      color: ${COLORS.sapphire};
      margin-top: 3px;
    }
  `}</style>
);

// --- Animation Hook ---
const useIntersectionObserver = (options = {}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.1, ...options });

    if (elementRef.current) observer.observe(elementRef.current);
    return () => observer.disconnect();
  }, [options]);

  return [elementRef, isVisible] as const;
};

// --- UI Components ---

const Button = ({ children, variant = 'primary', className = '', icon: Icon, ...props }: any) => {
  const base = "inline-flex items-center justify-center px-8 py-3.5 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm tracking-wide";
  
  const styles = variant === 'primary' 
    ? { 
        backgroundColor: COLORS.sapphire, 
        color: 'white',
        boxShadow: `0 10px 30px -5px rgba(47, 126, 218, 0.4)` 
      }
    : variant === 'outline'
    ? { 
        backgroundColor: 'rgba(255,255,255,0.5)', 
        color: COLORS.darkText, 
        border: `1px solid ${COLORS.windweaver}`,
        backdropFilter: 'blur(4px)'
      }
    : variant === 'dark'
    ? {
        backgroundColor: '#0F172A',
        color: 'white',
        boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.4)'
    }
    : {};

  return (
    <button className={`${base} ${className} hover:opacity-90 hover:-translate-y-0.5`} style={styles} {...props}>
      {children}
      {Icon && <Icon className="ml-2 w-4 h-4" />}
    </button>
  );
};

// --- Pricing Hero ---
const PricingHero = () => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <section className="relative pt-32 md:pt-44 pb-16 md:pb-20 px-4 overflow-hidden flex flex-col items-center">
      {/* Background Ambience */}
      <div className="glass-orb w-24 h-24 md:w-32 md:h-32 top-32 right-[5%] md:right-[15%] opacity-40"></div>
      <div className="glass-orb w-16 h-16 md:w-20 md:h-20 bottom-20 left-[5%] md:left-[10%] opacity-30"></div>

      <div className="max-w-4xl mx-auto text-center relative z-10" ref={ref}>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full glass-pill mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
           <Zap className="w-4 h-4 text-[#2F7EDA] fill-current" />
           <span className="text-xs font-bold text-[#555663] uppercase tracking-wider">Simple, Transparent Pricing</span>
        </div>

        <h1 className={`text-4xl md:text-6xl lg:text-7xl font-bold text-[#0F172A] leading-[1.1] mb-6 md:mb-8 tracking-tight transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          Pricing that Scales with <br/>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2F7EDA] to-[#5a9ef5]">Shelf Impact</span>
        </h1>

        <p className={`text-base md:text-xl text-[#555663] mb-10 md:mb-12 max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          Reflow AI is built to improve what matters most: Availability, Share of Voice, Competitive Edge, and Demand Capture. Start lean. <br /> Scale with intelligence.
        </p>
      </div>

      <style>{`
        .glass-pill {
          background: rgba(252, 253, 253, 0.6); 
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.8);
          box-shadow: 0 10px 40px -10px rgba(47, 126, 218, 0.1);
        }
      `}</style>
    </section>
  );
};

// --- Pricing Tiers ---
const PricingTiers = () => {
  const { openCalModal } = useCal();

  return (
    <section className="pb-20 md:pb-32 px-4">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 items-start">
        
        {/* Starter Plan */}
        <div className="glass-card p-6 md:p-8 rounded-[40px] flex flex-col h-full hover:border-[#2F7EDA]/50 transition-colors relative group order-2 lg:order-1">
          <div className="mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#EDEFF3] flex items-center justify-center text-[#2F7EDA] mb-6 group-hover:scale-110 transition-transform">
              <Zap className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold text-[#0F172A] mb-2">Starter</h3>
            <p className="text-sm text-[#555663] mb-6 min-h-[40px]">For growing brands that want control over digital shelf performance.</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-3xl md:text-4xl font-bold text-[#0F172A]">₹60,000</span>
              <span className="text-[#9FA0B5] font-medium">/month</span>
            </div>
            <p className="text-xs text-[#9FA0B5] font-semibold mb-6">Up to 25 SKUs • 6-7 Cities</p>
            <Button className="w-full" variant="outline" onClick={openCalModal}>Schedule a Call</Button>
          </div>
          
          <div className="border-t border-[#EDEFF3] pt-6 flex-grow">
            <div className="text-xs font-bold text-[#2F7EDA] uppercase tracking-wider mb-4">Core Shelf Intelligence</div>
            <ul className="pricing-feature-list mb-6">
              <li><CheckCircle2 className="w-4 h-4" /> OSA Tracking by SKU & City</li>
              <li><CheckCircle2 className="w-4 h-4" /> Share of Voice (Organic + Paid)</li>
              <li><CheckCircle2 className="w-4 h-4" /> Competitor Rank Benchmarking</li>
            </ul>

            <div className="text-xs font-bold text-[#2F7EDA] uppercase tracking-wider mb-4">Control & Insights</div>
            <ul className="pricing-feature-list">
              <li><CheckCircle2 className="w-4 h-4" /> Real-time Stock-out Alerts</li>
              <li><CheckCircle2 className="w-4 h-4" /> Competitor Discount Tracking</li>
              <li><CheckCircle2 className="w-4 h-4" /> Basic Automated Rules</li>
            </ul>
          </div>
        </div>

        {/* Growth Plan (Highlighted) */}
        <div className="glass-card p-6 md:p-8 rounded-[40px] flex flex-col h-full border-[#2F7EDA] shadow-[0_20px_60px_-15px_rgba(47,126,218,0.15)] relative transform lg:-translate-y-6 z-10 bg-gradient-to-b from-white to-[#f0f9ff] order-1 lg:order-2 mb-8 lg:mb-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#2F7EDA] text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
            Most Popular
          </div>
          
          <div className="mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#2F7EDA] flex items-center justify-center text-white mb-6">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold text-[#0F172A] mb-2">Growth</h3>
            <p className="text-sm text-[#555663] mb-6 min-h-[40px]">For brands operating across multiple cities with deeper SKU portfolios.</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-3xl md:text-4xl font-bold text-[#0F172A]">Custom</span>
            </div>
            <p className="text-xs text-[#9FA0B5] font-semibold mb-6">200+ SKUs • All Dark Stores</p>
            <Button className="w-full" onClick={openCalModal}>Schedule a Call</Button>
          </div>
          
          <div className="border-t border-[#EDEFF3] pt-6 flex-grow">
             <div className="text-sm font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#2F7EDA]"></div> Everything in Starter, plus:
             </div>

            <div className="text-xs font-bold text-[#2F7EDA] uppercase tracking-wider mb-4">Advanced Intelligence</div>
            <ul className="pricing-feature-list mb-6">
              <li><CheckCircle2 className="w-4 h-4" /> Weighted Category Discount Tracking</li>
              <li><CheckCircle2 className="w-4 h-4" /> Organic vs Ad SOV Decomposition</li>
              <li><CheckCircle2 className="w-4 h-4" /> Keyword Opportunity Finder</li>
              <li><CheckCircle2 className="w-4 h-4" /> Competitor Promotion Mapping</li>
            </ul>

            <div className="text-xs font-bold text-[#2F7EDA] uppercase tracking-wider mb-4">Automation at Scale</div>
            <ul className="pricing-feature-list">
              <li><CheckCircle2 className="w-4 h-4" /> Goal-based Ad Automation</li>
              <li><CheckCircle2 className="w-4 h-4" /> One-click Hyperlocal Campaigns</li>
              <li><CheckCircle2 className="w-4 h-4" /> Supply Chain Leakage Alerts</li>
              <li><CheckCircle2 className="w-4 h-4" /> Forecast-led Demand Planning</li>
            </ul>
          </div>
        </div>

        {/* Enterprise Plan */}
        <div className="glass-card p-6 md:p-8 rounded-[40px] flex flex-col h-full hover:border-[#2F7EDA]/50 transition-colors group order-3 lg:order-3">
          <div className="mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#0F172A] flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
              <Globe className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold text-[#0F172A] mb-2">Enterprise</h3>
            <p className="text-sm text-[#555663] mb-6 min-h-[40px]">For brands operating across geographies or managing complex catalogs.</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-3xl md:text-4xl font-bold text-[#0F172A]">Custom</span>
            </div>
            <p className="text-xs text-[#9FA0B5] font-semibold mb-6">Global Deployment • Omnichannel</p>
            <Button className="w-full" variant="dark" onClick={openCalModal}>Schedule a Call</Button>
          </div>
          
          <div className="border-t border-[#EDEFF3] pt-6 flex-grow">
             <div className="text-sm font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0F172A]"></div> Everything in Growth, plus:
             </div>

            <div className="text-xs font-bold text-[#2F7EDA] uppercase tracking-wider mb-4">Enterprise Grade</div>
            <ul className="pricing-feature-list mb-6">
              <li><CheckCircle2 className="w-4 h-4" /> Custom Data Integrations</li>
              <li><CheckCircle2 className="w-4 h-4" /> Multi-country Benchmarking</li>
              <li><CheckCircle2 className="w-4 h-4" /> Full API Access</li>
              <li><CheckCircle2 className="w-4 h-4" /> Advanced Demand Modeling</li>
              <li><CheckCircle2 className="w-4 h-4" /> Dedicated Account Team</li>
              <li><CheckCircle2 className="w-4 h-4" /> SLA-backed Support</li>
            </ul>
          </div>
        </div>

      </div>
    </section>
  );
};

// --- Why Pricing Scales Section ---
const WhyScalesSection = () => {
  return (
    <section className="py-16 md:py-24 bg-white/50 border-y border-[#C6D1D7]/30 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="order-2 md:order-1">
            <h3 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-6">Why pricing scales with impact.</h3>
            <p className="text-base md:text-lg text-[#555663] mb-6">
              Reflow AI does not price on dashboards. <br/>
              <span className="text-[#2F7EDA] font-bold">It prices on digital shelf influence.</span>
            </p>
            <p className="text-[#555663] leading-relaxed mb-8">
              As SKU count, city depth, and competitive complexity increase, the system processes more signals across multiple dimensions. More signals mean stronger automation and better demand capture.
            </p>
            <div className="flex flex-wrap gap-3">
               {['OSA', 'Organic SOV', 'Ad SOV', 'Competitor Discounting', 'Rank Movement', 'Demand Forecasts'].map((tag, i) => (
                 <span key={i} className="px-3 py-1.5 md:px-4 md:py-2 bg-white border border-[#EDEFF3] rounded-full text-xs font-bold text-[#0F172A] shadow-sm">
                   {tag}
                 </span>
               ))}
            </div>
          </div>
          
          {/* Visual Representation */}
          <div className="relative order-1 md:order-2">
             <div className="absolute inset-0 bg-gradient-to-r from-[#2F7EDA]/10 to-transparent rounded-[40px] blur-3xl transform rotate-3"></div>
             <div className="glass-card rounded-[40px] p-6 md:p-8 border border-[#EDEFF3] relative">
                <div className="space-y-4">
                  {[
                    { label: "Data Signals Processed", val: "1.2M+", icon: Activity },
                    { label: "Automation Triggers", val: "450/day", icon: Zap },
                    { label: "Revenue Protected", val: "₹4.5Cr", icon: ShieldCheck }
                  ].map((stat, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-white/60 rounded-2xl border border-[#EDEFF3]">
                       <div className="flex items-center gap-3 md:gap-4">
                          <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-[#EDEFF3] flex items-center justify-center text-[#2F7EDA]">
                             <stat.icon className="w-4 h-4 md:w-5 md:h-5" />
                          </div>
                          <span className="font-bold text-sm md:text-base text-[#555663]">{stat.label}</span>
                       </div>
                       <span className="font-bold text-sm md:text-base text-[#0F172A]">{stat.val}</span>
                    </div>
                  ))}
                </div>
             </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- FAQ Section ---
const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-[#EDEFF3] last:border-0">
      <button 
        className="w-full py-6 flex items-center justify-between text-left focus:outline-none group"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`text-base md:text-lg font-bold transition-colors pr-4 ${isOpen ? 'text-[#2F7EDA]' : 'text-[#0F172A] group-hover:text-[#2F7EDA]'}`}>
          {question}
        </span>
        {isOpen ? <ChevronUp className="text-[#2F7EDA] flex-shrink-0" /> : <ChevronDown className="text-[#9FA0B5] flex-shrink-0" />}
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'}`}>
        <p className="text-sm md:text-base text-[#555663] leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const faqs = [
    {
      q: "Do I need to sign a long-term contract?",
      a: "No. All plans are month-to-month. You can upgrade, downgrade, or cancel anytime. Annual billing is available for brands looking for cost savings."
    },
    {
      q: "What if I want to add more users or SKUs later?",
      a: "Plans are modular. You can expand SKU limits, cities, or automation layers without switching platforms. Pricing adjusts transparently based on scope."
    },
    {
      q: "Do you offer a free trial?",
      a: "Yes. The Starter plan includes a 14-day trial with full OSA tracking, Share of Voice monitoring, competitor discount visibility, and automated alert setup. No credit card required."
    },
    {
      q: "How is pricing determined for Growth and Enterprise?",
      a: "We design pricing around your operational complexity. Factors include SKU volume, number of cities, marketplace coverage, automation depth, and integration requirements."
    }
  ];

  return (
    <section className="py-16 md:py-24 px-4">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] text-center mb-8 md:mb-12">Frequently Asked Questions</h2>
        <div className="glass-card rounded-[40px] p-6 md:p-12">
           {faqs.map((faq, i) => (
             <FAQItem key={i} question={faq.q} answer={faq.a} />
           ))}
        </div>
      </div>
    </section>
  );
};

// --- Bottom CTA Component ---
const BottomCTA = () => {
  const { openCalModal } = useCal();

  return (
    <section className="py-16 md:py-20 px-4 text-center">
      <div className="max-w-4xl mx-auto glass-card rounded-[60px] p-10 md:p-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#2F7EDA] to-[#5a9ef5]"></div>
        
        <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] mb-6 md:mb-8 leading-tight">Own your digital shelf <br/>before your competitors do.</h2>
        <div className="flex flex-wrap justify-center gap-4 mb-8 md:mb-10 text-sm font-semibold text-[#555663]">
           <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2F7EDA]" /> Measure OSA</span>
           <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2F7EDA]" /> Control SOV</span>
           <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2F7EDA]" /> Act on Signals</span>
           <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2F7EDA]" /> Automate Execution</span>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button icon={ArrowRight} onClick={openCalModal}>Schedule a Call</Button>
            <Button variant="outline" onClick={openCalModal}>Book a Demo</Button>
        </div>
      </div>
    </section>
  );
};

// --- Main Page Component ---
export function PricingLandingPage() {

  return (
    <>
      <GlobalStyles />
      <div className="min-h-screen relative selection:bg-[#2F7EDA] selection:text-white">
        
        {/* Fixed Halo Background */}
        <div className="halo-container">
          <div className="halo-gradient w-[800px] h-[800px] bg-[#2F7EDA] top-[-20%] left-[-10%] opacity-20"></div>
          <div className="halo-gradient w-[600px] h-[600px] bg-[#9FA0B5] bottom-[-10%] right-[-10%] opacity-10"></div>
          <div className="halo-gradient w-[400px] h-[400px] bg-[#C6D1D7] top-[40%] left-[30%] opacity-30"></div>
        </div>

        <LandingNavbar />
        
        <main>
          <PricingHero />
          
          <PricingTiers />

        <WhyScalesSection />

        <FAQSection />

        {/* Bottom CTA */}
        <BottomCTA />
        </main>

        <LandingFooter />
      </div>
    </>
  );
}
